import { WorkLineDto, WorkOrderObjectHierarchyDto } from '@app/generated/model';
import {
  MarkWorkLineAsComplete,
  ReOpenWorkLine,
  UpdateWorkLineCableLength,
  UpdateWorkLineProgress,
  UpdateWorkLineQuantity,
  WorkOrderActionTypes,
} from '@dwo/actions';

export type OptimisticWorkLineAction =
  | UpdateWorkLineProgress
  | UpdateWorkLineQuantity
  | UpdateWorkLineCableLength
  | MarkWorkLineAsComplete
  | ReOpenWorkLine;

export function applyWorkLineActionToHierarchy(
  hierarchy: WorkOrderObjectHierarchyDto[],
  action: OptimisticWorkLineAction,
): WorkOrderObjectHierarchyDto[] {
  return (hierarchy ?? []).map((objectNode) => updateObjectNode(objectNode, action));
}

export function applyWorkLineActionToSelectedWorkLines<T extends WorkLineDto>(
  workLines: T[],
  action: OptimisticWorkLineAction,
): T[] {
  return (workLines ?? []).map((workLine) => updateWorkLine({ ...workLine }, action) as T);
}

function updateObjectNode(
  objectNode: WorkOrderObjectHierarchyDto,
  action: OptimisticWorkLineAction,
): WorkOrderObjectHierarchyDto {
  const children = (objectNode.children ?? []).map((child) => updateObjectNode(child, action));
  const workLines = (objectNode.workLines ?? []).map((workLine) => updateWorkLine({ ...workLine }, action));

  const updatedObject: WorkOrderObjectHierarchyDto = {
    ...objectNode,
    children,
    workLines,
  };

  if (action.type === WorkOrderActionTypes.UpdateWorkLineProgress) {
    updatedObject.progress = calculateObjectProgress(updatedObject);
  }

  return updatedObject;
}

function updateWorkLine(workLine: WorkLineDto, action: OptimisticWorkLineAction): WorkLineDto {
  switch (action.type) {
    case WorkOrderActionTypes.UpdateWorkLineProgress:
      if (action.workLineIds.some((workLineId) => workLineId === workLine.id)) {
        workLine.progress = action.progress;
      }
      return workLine;

    case WorkOrderActionTypes.UpdateWorkLineQuantity:
      if (action.workLineId === workLine.id) {
        workLine.installedQuantity = action.quantity;
      }
      return workLine;

    case WorkOrderActionTypes.UpdateWorkLineCableLength:
      if (action.workLineId === workLine.id) {
        workLine.installedQuantity = action.length;
      }
      return workLine;

    case WorkOrderActionTypes.MarkWorkLineAsComplete:
      if (action.workLineId === workLine.id) {
        workLine.completed = true;
      }
      return workLine;

    case WorkOrderActionTypes.ReOpenWorkLine:
      if (action.workLineId === workLine.id) {
        workLine.completed = false;
      }
      return workLine;

    default:
      return workLine;
  }
}

function calculateObjectProgress(objectNode: WorkOrderObjectHierarchyDto): number {
  const totalHours = getObjectHours(objectNode);

  if (totalHours <= 0) {
    return objectNode.progress ?? 0;
  }

  const workLineProgress = (objectNode.workLines ?? []).reduce((sum, workLine) => {
    const estimatedHours = workLine.estimatedHours ?? 0;
    const weight = estimatedHours > 0 ? estimatedHours / totalHours : 1;
    return sum + (workLine.progress ?? 0) * weight;
  }, 0);

  const childrenProgress = (objectNode.children ?? []).reduce((sum, child) => {
    const childHours = getObjectHours(child);
    const weight = childHours > 0 ? childHours / totalHours : 1;
    return sum + (child.progress ?? 0) * weight;
  }, 0);

  return workLineProgress + childrenProgress;
}

function getObjectHours(objectNode: WorkOrderObjectHierarchyDto): number {
  const workLineHours = (objectNode.workLines ?? []).reduce((sum, workLine) => sum + (workLine.estimatedHours ?? 0), 0);
  const childHours = (objectNode.children ?? []).reduce((sum, child) => sum + getObjectHours(child), 0);
  return workLineHours + childHours;
}
